import React, { useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import Ui from './components/Ui';
import WorkflowCanvas from './components/workflow3/WorkflowCanvas';
import FeedbackModal from './components/base/FeedbackModal';
import Dialog from './pages/dialog';
import Workflow from './pages/workflow';
import Meeting from './pages/meeting';
import SetupPage from './pages/setup/SetupPage';
import NotFoundPage from './pages/not-found/NotFoundPage';
import { DataState } from './context/DataProvider';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Projects from './ProjectsDemo';
import AdminPage from './pages/admin/AdminPage';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import ReportTemplates from './pages/report-templates/ReportTemplates';
import MeetingSummaryPage from './pages/meeting-summary/MeetingSummaryPage';
import MeetingApprovalsPage from './pages/meeting-approvals/MeetingApprovalsPage';
import ProjectRegisterPage from './pages/project-register/ProjectRegisterPage';
import DashboardPage from './pages/dashboards/DashboardPage';
import PublicDashboardPage from './pages/dashboards/PublicDashboardPage';
import SharePointDocumentTester from './pages/sharepoint-test/SharePointDocumentTester';
import DocumentChangeHistory from './pages/sharepoint-test/DocumentChangeHistory';
import {
  ENABLE_AI_CHAT,
  ENABLE_COMMUNICATION,
  ENABLE_DASHBOARDS,
  ENABLE_MAIL_INTEL,
  ENABLE_MEETING_APPROVALS_PAGE,
  ENABLE_MEETINGS,
  ENABLE_REPORT_TEMPLATES,
  ENABLE_SHAREPOINT,
  ENABLE_WORKFLOWS,
  ENABLE_ADMIN_SECTION,
} from './config/features';
import HomePage from './pages/home/HomePage';
import { useAccess } from './services/access';

const ProtectedRoutes = ({
  onLogin,
  handleStorageChange,
  onProjectAccess,
  projectIdToken,
  userIdToken,
  isTeamsInitialized,
}) => {
  const {
    feedbackModalOpen,
    setFeedbackModalOpen,
    theme,
    justChangedLocalStorageData,
    setJustChangedLocalStorageData,
  } = DataState(); // Example of context usage

  useEffect(() => {
    if (justChangedLocalStorageData) {
      handleStorageChange();
      setJustChangedLocalStorageData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justChangedLocalStorageData]);

  // Chat needs the feature and a role that may use it; a guest lands on the
  // dashboard instead.
  const { can } = useAccess();
  const chatEnabled = ENABLE_AI_CHAT && can('chat:send');
  const dashboardEnabled = ENABLE_MEETINGS && can('meeting:view');
  const noChatRoute = ENABLE_COMMUNICATION
    ? '/communication'
    : ENABLE_WORKFLOWS
      ? '/workflow'
      : dashboardEnabled
        ? '/analytics'
        : '/home';
  const noHubRoute = ENABLE_WORKFLOWS
    ? '/workflow'
    : dashboardEnabled
      ? '/analytics'
      : '/home';

  const handleFeedbackSubmit = () => {
    toast.success('Feedback Sent');
  };

  return (
    <div className="app" data-theme={theme}>
      <Routes>
        <Route
          path="/"
          element={
            <Navigate to={chatEnabled ? '/chat' : noChatRoute} replace />
          }
        />
        <Route
          path="/login"
          element={
            <Login onLogin={onLogin} isTeamsInitialized={isTeamsInitialized} />
          }
        />
        {/* Route always registered — backend enforces who can complete signup */}
        <Route path="/sign-up" element={<Register onLogin={onLogin} />} />
        {chatEnabled ? (
          <>
            <Route path="/chat" element={<Ui />} />
            <Route path="/chat/:taskId" element={<Ui />} />
          </>
        ) : (
          <>
            <Route
              path="/chat"
              element={<Navigate to={noChatRoute} replace />}
            />
            <Route
              path="/chat/:taskId"
              element={<Navigate to={noChatRoute} replace />}
            />
          </>
        )}
        {ENABLE_COMMUNICATION ? (
          <Route path="/communication" element={<Dialog />} />
        ) : (
          <Route
            path="/communication"
            element={<Navigate to={noHubRoute} replace />}
          />
        )}
        <Route
          path="/workflow-canvas"
          element={
            ENABLE_WORKFLOWS ? (
              <WorkflowCanvas />
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />
        {ENABLE_WORKFLOWS ? (
          <Route path="/workflow" element={<Workflow />} />
        ) : (
          <Route path="/workflow" element={<Navigate to="/home" replace />} />
        )}
        <Route
          path="/home"
          element={
            chatEnabled ? (
              <Navigate to="/chat" replace />
            ) : noChatRoute !== '/home' ? (
              <Navigate to={noChatRoute} replace />
            ) : (
              <HomePage />
            )
          }
        />
        <Route
          path="/projects"
          element={
            <Projects
              userIdToken={userIdToken}
              onProjectAccess={onProjectAccess}
              handleStorageChange={handleStorageChange}
            />
          }
        />
        {ENABLE_MEETINGS && <Route path="/meeting" element={<Meeting />} />}
        {/* Human approval queues from the meeting pipeline: follow-up
            proposals awaiting a decision, clarifications awaiting an
            answer. Gated with the other meeting screens. */}
        {ENABLE_MEETINGS && ENABLE_MEETING_APPROVALS_PAGE && (
          <Route path="/meeting-approvals" element={<MeetingApprovalsPage />} />
        )}
        {/* What the pipeline concluded, as opposed to /meeting-approvals
            (what it is still asking a human about). Each section gets its
            own URL (see ANALYTICS_TABS) so the sidebar nav can deep-link
            and the browser back/forward works between them; all five
            render the same page, which reads the section off the path. */}
        {ENABLE_MEETINGS && (
          <>
            {/* Bare /analytics is not a section itself — send it to the
                default section so every section has its own real URL,
                Insights included, instead of Insights being a special case
                that just happens to live at the parent path. */}
            <Route
              path="/analytics"
              element={<Navigate to="/analytics/insights" replace />}
            />
            <Route
              path="/analytics/insights"
              element={<ProjectRegisterPage />}
            />
            <Route
              path="/analytics/commitments"
              element={<ProjectRegisterPage />}
            />
            <Route
              path="/analytics/approvals"
              element={<ProjectRegisterPage />}
            />
            <Route
              path="/analytics/dependencies"
              element={<ProjectRegisterPage />}
            />
            <Route path="/analytics/risks" element={<ProjectRegisterPage />} />
            <Route path="/analytics/sla" element={<ProjectRegisterPage />} />
            {ENABLE_MAIL_INTEL && (
              <Route
                path="/analytics/mail"
                element={
                  can('mail_intel:inbox:view') ? (
                    <ProjectRegisterPage />
                  ) : (
                    <Navigate to="/analytics/insights" replace />
                  )
                }
              />
            )}
          </>
        )}
        {/* Old URLs, kept as redirects so existing bookmarks/links still work */}
        {ENABLE_MEETINGS && (
          <>
            <Route
              path="/project-register"
              element={<Navigate to="/analytics/insights" replace />}
            />
            <Route
              path="/dashboard"
              element={<Navigate to="/analytics/insights" replace />}
            />
            <Route
              path="/dashboard/insights"
              element={<Navigate to="/analytics/insights" replace />}
            />
            <Route
              path="/dashboard/commitments"
              element={<Navigate to="/analytics/commitments" replace />}
            />
            <Route
              path="/dashboard/dependencies"
              element={<Navigate to="/analytics/dependencies" replace />}
            />
            <Route
              path="/dashboard/risks"
              element={<Navigate to="/analytics/risks" replace />}
            />
            <Route
              path="/dashboard/sla"
              element={<Navigate to="/analytics/sla" replace />}
            />
          </>
        )}
        <Route path="/setup" element={<SetupPage />} />
        {ENABLE_REPORT_TEMPLATES && (
          <Route path="/report-templates" element={<ReportTemplates />} />
        )}
        {/* Chat-built dashboards (docs/DASHBOARD_BUILDER.md). */}
        {ENABLE_DASHBOARDS && can('dashboard:view') && (
          <Route path="/dashboards/:dashboardId" element={<DashboardPage />} />
        )}
        {/* Public snapshot links work signed in too (no permission needed). */}
        {ENABLE_DASHBOARDS && (
          <Route path="/d/:token" element={<PublicDashboardPage />} />
        )}
        {/* Both read SharePoint document records (file:view); without it
            they are not pages at all rather than pages that fail to load. */}
        {ENABLE_SHAREPOINT && can('file:view') && (
          <Route
            path="/sharepoint-test"
            element={<SharePointDocumentTester userIdToken={userIdToken} />}
          />
        )}
        {ENABLE_SHAREPOINT && can('file:view') && (
          <Route
            path="/document-history"
            element={<DocumentChangeHistory userIdToken={userIdToken} />}
          />
        )}
        {ENABLE_MEETINGS && (
          <Route
            path="/meeting-summary/:eventId"
            element={<MeetingSummaryPage />}
          />
        )}
        {/* --- CORRECTED ADMIN ROUTE --- */}
        {/* Pass userIdToken to the protected route wrapper */}
        {ENABLE_ADMIN_SECTION && (
          <Route
            path="/admin"
            element={
              <ProtectedAdminRoute userIdToken={userIdToken}>
                <AdminPage />
              </ProtectedAdminRoute>
            }
          />
        )}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default ProtectedRoutes;
